import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";

const currentYear = new Date().getFullYear();
const YOB_OPTIONS = Array.from({ length: 80 }, (_, i) => currentYear - 10 - i);

export default function SocialSetupPage() {
  const nav = useNavigate();
  const [handle, setHandle] = useState("");
  const [yob, setYob] = useState("");
  const [hasYob, setHasYob] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me/social").then((r) => {
      if (r.data.social_setup_complete) {
        nav("/student/friends", { replace: true });
        return;
      }
      setHasYob(!!r.data.year_of_birth);
      if (r.data.year_of_birth) setYob(String(r.data.year_of_birth));
    });
  }, [nav]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { display_handle: handle };
      if (!hasYob && yob) payload.year_of_birth = parseInt(yob, 10);
      await api.post("/me/social/setup", payload);
      toast.success(`Welcome, ${handle}!`);
      nav("/student/friends", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-6" data-testid="social-setup-page">
      <div className="text-center">
        <div className="mx-auto w-12 h-12 grid place-items-center rounded-full bg-indigo-100 text-indigo-700 mb-3">
          <Users className="w-6 h-6" />
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          Set up your social profile
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          You'll use a public display handle for friends + QR codes. We never share your
          full name, email, or phone with other students.
        </p>
      </div>

      <Card className="p-6 border-slate-200">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Display Handle
            </label>
            <Input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="e.g. JuanReviewer2026"
              maxLength={20}
              className="mt-1.5"
              data-testid="social-setup-handle-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              3–20 chars · letters, digits, underscores, dots · must start with a letter
            </p>
          </div>

          {!hasYob && (
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Year of Birth
              </label>
              <Select value={yob} onValueChange={setYob}>
                <SelectTrigger className="mt-1.5" data-testid="social-setup-yob-select">
                  <SelectValue placeholder="Pick year" />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {YOB_OPTIONS.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Used to apply safer defaults for under-18 students. Not shown to others.
              </p>
            </div>
          )}

          <div className="text-[11px] text-slate-500 p-3 bg-slate-50 rounded-md border border-slate-200">
            By continuing you agree to the{" "}
            <Link to="/legal/community-guidelines" className="text-indigo-600 hover:underline">
              Community Guidelines
            </Link>
            : no harassment, no impersonation, no adult content. Reports = action within 24 hours.
          </div>

          <Button
            className="w-full h-11 bg-[#1E3A8A] hover:bg-[#1E40AF]"
            onClick={submit}
            disabled={busy || !handle.trim() || (!hasYob && !yob)}
            data-testid="social-setup-submit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Continue
          </Button>
        </div>
      </Card>
    </div>
  );
}
